import React, { useCallback, HTMLAttributes, useState, useMemo, SyntheticEvent, forwardRef, ForwardedRef, MouseEventHandler } from 'react';
import {
	Autocomplete,
	AutocompleteRenderInputParams,
	AutocompleteRenderOptionState,
	AutocompleteRenderGetTagProps,
	AutocompleteProps,
	AutocompleteValue,
	SxProps,
	Theme,
	InputAdornment,
	Popper,
	PopperProps,
} from '@mui/material';
import { CoreTextFieldProps } from '../core-textfield';
import { Option } from './core-autocomplete.types';
import { coreAutocompleteStyles } from './core-autocomplete.styles';
import { selectPaperStyles } from '../core-select/core-select.styles';
import { CoreSelectMenuItem } from '../core-select/core-select-menu-item.component';
import { CoreAutocompleteChip } from './core-autocomplete-chip.component';
import { difference } from 'lodash-es';
import { flatten } from 'lodash-es';
import { CoreAutocompleteInput } from './core-autocomplete-input.component';
import { Colors } from '../../../constants';
import { isEqual } from 'lodash-es';
import { ArrowDownIcon, CloseIcon } from '../../../icons';
import { clsx } from '../../../utils';

type TextFieldProps = Pick<
	CoreTextFieldProps,
	'disabled' | 'fullWidth' | 'label' | 'helperText' | 'error' | 'controlSx' | 'placeholder' | 'requiredMark' | 'id'
>;

export interface CoreAutocompleteProps<
	Value extends object,
	Multiple extends boolean | undefined,
	DisableClearable extends boolean | undefined
> extends TextFieldProps,
		Pick<
			AutocompleteProps<Value, Multiple, DisableClearable, false>,
			| 'value'
			| 'inputValue'
			| 'onChange'
			| 'multiple'
			| 'disableClearable'
			| 'disableCloseOnSelect'
			| 'loading'
			| 'noOptionsText'
			| 'freeSolo'
			| 'getOptionLabel'
			| 'getOptionKey'
			| 'getOptionDisabled'
			| 'PopperComponent'
		> {
	sx?: SxProps<Theme>;
	paperSx?: SxProps<Theme>;
	labelSx?: SxProps<Theme>;
	options?: Value[];
	allOption?: Value;
	showSelectedCount?: boolean;
	hideInput?: boolean;
	startAdornment?: React.ReactNode;
	popperPlacement?: PopperProps['placement'];
	customOptionContainer?: boolean;
	onBlur?(e: SyntheticEvent): void;
	onOpen?(): void;
	onClose?(): void;
	onInputChange?(value: string): void;
	renderOption?(option: Value, state: AutocompleteRenderOptionState, onClick?: MouseEventHandler<HTMLElement>): React.ReactNode;
	getOptionIcon?(option?: Value): React.JSX.Element;
	overrideRenderOption?(
		props: HTMLAttributes<HTMLLIElement>,
		option: Value,
		state: AutocompleteRenderOptionState
	): React.JSX.Element;
	renderTags?(tagValues: Value[], getTagProps: AutocompleteRenderGetTagProps): React.ReactNode;
}

export const CoreAutocomplete = forwardRef(
	<Multiple extends boolean | undefined, DisableClearable extends boolean | undefined, Value extends object = Option>(
		props: CoreAutocompleteProps<Value, Multiple, DisableClearable>,
		ref: ForwardedRef<HTMLElement>
	) => {
		const {
			sx,
			paperSx,
			onBlur,
			options = [],
			allOption,
			disabled,
			fullWidth,
			label,
			labelSx,
			helperText,
			error,
			controlSx,
			placeholder,
			id,
			loading,
			disableClearable,
			onOpen,
			multiple,
			onChange,
			disableCloseOnSelect,
			value = multiple ? ([] as AutocompleteValue<Value, Multiple, DisableClearable, false>) : undefined,
			requiredMark,
			noOptionsText,
			onClose,
			showSelectedCount = false,
			onInputChange,
			renderOption: outerRenderOption,
			overrideRenderOption,
			renderTags,
			hideInput,
			startAdornment,
			freeSolo,
			getOptionLabel = opt => (opt as Option)?.label,
			getOptionKey = opt => (opt as Option)?.value,
			getOptionIcon = opt => (opt as Option)?.icon,
			getOptionDisabled,
			PopperComponent,
			popperPlacement = 'bottom-end',
			customOptionContainer,
		} = props;

		const [open, setOpen] = useState(false);

		const extendedOptions = useMemo(() => [...(allOption ? [allOption] : []), ...options], [allOption, options]);

		const allOptionsSelected = useMemo(
			() =>
				difference(
					options.map(x => getOptionKey(x)),
					flatten(value ? [value] : []).map(x => getOptionKey(x as Value))
				).length === 0,
			[options, value, getOptionKey]
		);

		const handleAllOptionClick = useCallback(
			(e: React.SyntheticEvent) => {
				if (!multiple) {
					return;
				}

				if (allOptionsSelected) {
					onChange?.(e, [] as Array<Value> as AutocompleteValue<Value, Multiple, DisableClearable, false>, 'clear');
				} else {
					onChange?.(e, options as AutocompleteValue<Value, Multiple, DisableClearable, false>, 'selectOption');
				}
			},
			[multiple, allOptionsSelected, onChange, options]
		);

		const handleOpen = useCallback(() => {
			setOpen(true);
			onOpen?.();
		}, [onOpen]);

		const handleClose = useCallback(() => {
			setOpen(false);
			onClose?.();
		}, [onClose]);

		const renderInput = useCallback(
			(inputParams: AutocompleteRenderInputParams) => (
				<CoreAutocompleteInput
					{...inputParams}
					label={label}
					labelSx={labelSx}
					error={error}
					controlSx={controlSx}
					sx={sx}
					showSelectedCount={showSelectedCount}
					value={value}
					requiredMark={requiredMark}
					placeholder={placeholder}
					loading={loading}
					helperText={helperText}
					disabled={disabled ?? false}
					onInputChange={onInputChange}
					hide={hideInput}
					startAdornment={startAdornment}
					inputProps={{
						...inputParams.inputProps,
						value:
							inputParams.inputProps.value === 'undefined' || !inputParams.inputProps.value
								? ''
								: inputParams.inputProps.value,
					}}
					InputProps={{
						...inputParams.InputProps,
						endAdornment: inputParams.InputProps.endAdornment,
						startAdornment: (
							<>
								{startAdornment && <InputAdornment position='start' sx={{ minHeight: '16px', minWidth: '16px' }}>{startAdornment}</InputAdornment>}
								{inputParams.InputProps.startAdornment}
							</>
						),
					}}
				/>
			),
			[
				label,
				error,
				controlSx,
				sx,
				showSelectedCount,
				value,
				requiredMark,
				placeholder,
				loading,
				helperText,
				disabled,
				onInputChange,
				hideInput,
				startAdornment,
				labelSx,
			]
		);

		const renderOption = useCallback(
			(props: HTMLAttributes<HTMLLIElement>, option: Value, state: AutocompleteRenderOptionState) => {
				if (overrideRenderOption) {
					return overrideRenderOption(props, option, state);
				}

				const { selected } = state;
				const label = getOptionLabel(option);
				const value = getOptionKey(option);
				const isAllOption = allOption && value === getOptionKey(allOption);

				return (
					<CoreSelectMenuItem
						{...props}
						key={value}
						noWrap
						customContainer={customOptionContainer}
						withCheckbox={multiple && !customOptionContainer}
						selected={isAllOption ? allOptionsSelected : selected}
						onClick={isAllOption ? handleAllOptionClick : props.onClick}
						disabled={getOptionDisabled?.(option)}
					>
						{outerRenderOption ? outerRenderOption(option, state, props.onClick as any) : label}
					</CoreSelectMenuItem>
				);
			},
			[
				overrideRenderOption,
				getOptionLabel,
				getOptionKey,
				allOption,
				allOptionsSelected,
				multiple,
				handleAllOptionClick,
				getOptionDisabled,
				outerRenderOption,
				customOptionContainer,
			]
		);

		const renderTag = useCallback(
			(tagValues: Value[], getTagProps: AutocompleteRenderGetTagProps) => {
				if (showSelectedCount) return undefined;

				if (renderTags) {
					return renderTags?.(tagValues, getTagProps);
				}

				return tagValues.map((option, index) => {
					const { onDelete, ...props } = getTagProps({ index });

					const label = getOptionLabel(option);
					const icon = getOptionIcon(option);

					return (
						<CoreAutocompleteChip
							{...props}
							onDelete={onDelete}
							label={label}
							icon={
								<span
									className='MuiChip-icon'
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									{icon}
								</span>
							}
							key={props.key}
						/>
					);
				});
			},
			[renderTags, showSelectedCount, getOptionLabel, getOptionIcon]
		);

		const isOptionEqualToValue = useCallback(
			(option: Value, value: Value) => isEqual(getOptionKey(option), getOptionKey(value)),
			[getOptionKey]
		);

		return (
			<Autocomplete
				id={id}
				ref={ref}
				open={open}
				onBlur={onBlur}
				onOpen={handleOpen}
				value={value}
				freeSolo={freeSolo}
				isOptionEqualToValue={isOptionEqualToValue}
				multiple={multiple}
				onChange={(...params) => {
					onChange?.(...params);
				}}
				getOptionKey={getOptionKey}
				getOptionLabel={getOptionLabel}
				disableCloseOnSelect={disableCloseOnSelect}
				onClose={handleClose}
				loadingText={'Loading'}
				noOptionsText={noOptionsText || 'No options'}
				disabled={disabled}
				sx={coreAutocompleteStyles(Array.isArray(value) ? !!value?.length : !!value)}
				fullWidth={fullWidth}
				renderOption={renderOption}
				PopperComponent={
					PopperComponent ??
					(props => (
						<Popper
							{...props}
							placement={popperPlacement}
							style={{
								...(props.style ?? {}),
								minWidth: '280px',
							}}
						/>
					))
				}
				slotProps={{
					paper: {
						sx: clsx(selectPaperStyles, paperSx),
					},
				}}
				renderTags={renderTag}
				renderInput={renderInput}
				options={extendedOptions}
				forcePopupIcon
				disableClearable={disableClearable}
				loading={loading}
				clearIcon={
					<CloseIcon
						width={24}
						height={24}
						style={{
							color: disabled ? Colors.grayDark : Colors.white,
						}}
					/>
				}
				popupIcon={
					<ArrowDownIcon
						width={24}
						height={24}
						style={{
							color: disabled ? Colors.grayDark : Colors.white,
						}}
					/>
				}
			/>
		);
	}
);

CoreAutocomplete.displayName = 'CoreAutocomplete';
